import {
  DvCheckboxCellEditorParams,
  DvCheckboxCellRenderer,
  DvColumnType,
  dvColumnTypes,
  DvIconCellRenderer,
  DvIconHeaderRenderer,
  DvRadioButtonCellRenderer,
  DvValueFormatterUtils,
} from '@devinco/ui/grid';
import { TranslateService } from '@ngx-translate/core';
import { TripTypeIds } from '@pages/trips-page/constants';
import { ThumbnailRendererComponent } from '@shared/components/thumbnail-renderer/thumbnail-renderer.component';
import {
  UserStatusIcons,
  UserStatusTranslationKey,
} from '@shared/constants/user-status';
import { WeekDay } from '@shared/constants/weekdays';
import { UserDto } from '@shared/interfaces/user.model';
import DateUtils from '@shared/utils/date.utils';
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

export const ColumnType = {
  ...DvColumnType,
  Checkbox: 'checkbox',
  Date: 'date',
  DateTime: 'dateTime',
  DateVT: 'dateVT',
  Dropdown: 'dropdown',
  Hidden: 'hidden',
  /** Mark ColDef as entity id field. Remember to set `colDef.field` */
  Id: 'id',
  Number: 'number',
  NumberDecimal: 'numberDecimal',
  NumberText: 'numberText',
  Status: 'status',
  Text: 'text',
  Tooltip: 'tooltip',
  /** Convert milage from meters to kilo meters. `222334400 => 223 334.40` */
  Km: 'km',
  Icon: 'icon',
  TranslatedText: 'translatedText',
  Thumbnail: 'thumbnail',
  RadioButton: 'radioButton',
  Duration: 'duration',
  TimeText: 'time',
  Invisible: 'invisible',
  TripType: 'tripType',
  UserStatus: 'userStatus',
};

// See https://www.ag-grid.com/angular-data-grid/grid-options/#reference-columns-columnTypes
export const getColumnTypes = (
  translateService: TranslateService
): { [name: string]: ColDef } => {
  return {
    ...dvColumnTypes,
    [ColumnType.Boolean]: {
      cellStyle: { 'text-align': 'center' },
      filter: 'agSetColumnFilter',
      width: 50,
    },
    [ColumnType.DateVT]: {
      valueFormatter: ({ colDef, data }) => {
        const { field } = colDef;
        if (field && data?.[field]) {
          return data[field]
            ? DateUtils.convertDateTimeToNO(new Date(data[field]), true)
            : '';
        }

        return '';
      },
      valueSetter: ({ colDef, data, newValue }) => {
        const { field } = colDef;
        if (field && data[field]) {
          data[field] = newValue + 'T00:00:00';
        }

        return true;
      },
      cellStyle: { 'text-align': 'right' },
      headerClass: 'ag-right-aligned-header',
    },
    [ColumnType.DateTime]: {
      valueFormatter: DvValueFormatterUtils.dateTime,
      cellEditorParams: { type: 'datetime-local' },
      cellStyle: { 'text-align': 'right' },
      headerClass: 'ag-right-aligned-header',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: (columnFilterValue: Date, cellValue: string) => {
          if (!cellValue) return -1;
          const columnFilterValueDateStr =
            DateUtils.convertDate(columnFilterValue);
          const cellValueDateStr = DateUtils.convertDate(new Date(cellValue));

          if (columnFilterValueDateStr === cellValueDateStr) {
            return 0;
          } else if (columnFilterValueDateStr > cellValueDateStr) {
            return -1;
          } else {
            return 1;
          }
        },
        inRangeInclusive: true,
      },
    },
    [ColumnType.Status]: {
      headerClass: 'text-center',
      width: 75,
    },
    [ColumnType.Hidden]: {
      hide: true,
    },
    [ColumnType.Invisible]: {
      hide: true,
      suppressColumnsToolPanel: true,
    },
    [ColumnType.Id]: {
      filter: 'agTextColumnFilter',
      cellStyle: { justifyContent: 'flex-end', display: 'flex' },
      headerClass: 'ag-right-aligned-header',
    },
    [ColumnType.Number]: {
      valueFormatter: DvValueFormatterUtils.number,
      filter: 'agNumberColumnFilter',
      cellStyle: { justifyContent: 'flex-end', display: 'flex' },
      headerClass: 'ag-right-aligned-header',
    },
    [ColumnType.NumberDecimal]: {
      valueFormatter: DvValueFormatterUtils.numberDecimal,
      cellStyle: { 'text-align': 'right' },
      filter: 'agNumberColumnFilter',
    },
    [ColumnType.NumberText]: {
      cellStyle: { justifyContent: 'flex-end', display: 'flex' },
      headerClass: 'ag-right-aligned-header',
      filter: 'agTextColumnFilter',
    },
    [ColumnType.Km]: {
      valueFormatter: params => {
        return params.value
          ? params.value.toLocaleString('no', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + ' km'
          : '';
      },
      cellStyle: { 'text-align': 'right' },
      headerClass: 'ag-right-aligned-header',
      filter: 'agNumberColumnFilter',
    },
    [ColumnType.Text]: {
      filter: 'agTextColumnFilter',
      comparator: caseInsensitiveTextComparator,
    },
    [ColumnType.Icon]: {
      cellRenderer: DvIconCellRenderer,
      headerComponent: DvIconHeaderRenderer,
      headerClass: 'text-center',
      width: 80,
    },
    [ColumnType.Checkbox]: {
      headerClass: 'text-center',
      cellRenderer: DvCheckboxCellRenderer,
      // while none of the grids has inline editing all checkboxes are with disabled state
      cellRendererParams: {
        disabled: true,
      } satisfies DvCheckboxCellEditorParams,
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value === true ? 'General.True' : 'General.False';
          return translateService.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
    },
    [ColumnType.TranslatedText]: {
      filter: 'agTextColumnFilter',
      comparator: caseInsensitiveTextComparator,
      valueGetter: ({ colDef, data }) => {
        const { field } = colDef;

        if (!field || (field && !data?.[field])) return '';
        if (field && data?.[field]) {
          const isWeekday = Object.values(WeekDay).includes(data[field]);
          if (isWeekday) {
            return translateService.instant(
              `General.DaysOfWeek.${data[field]}`
            );
          } else if (
            field === 'VehicleType' ||
            field === 'VehicleTypeDescription'
          ) {
            return translateService.instant(`VehicleType.${data[field]}`);
          } else if (field === 'TripType') {
            return translateService.instant(`TripType.${data[field]}`);
          } else if (field === 'ReportType') {
            return translateService.instant(`ReportType.${data[field]}`);
          } else if (field.toLowerCase().includes('status')) {
            return translateService.instant(`Status.${data[field]}`);
          }
        }

        return translateService.instant(field ? data[field] : '');
      },
      valueSetter: ({ colDef, data, newValue }) => {
        const { field } = colDef;
        if (field && data[field]) {
          const isWeekday = Object.values(WeekDay).includes(newValue);
          data[field] = translateService.instant(
            isWeekday ? `General.DaysOfWeek.${newValue}` : newValue
          );
        }

        return true;
      },
    },
    [ColumnType.Thumbnail]: {
      cellRenderer: ThumbnailRendererComponent,
      autoHeight: true,
      width: 80,
      cellStyle: { 'text-align': 'center' },
      headerClass: 'text-center',
    },
    [ColumnType.RadioButton]: {
      cellRenderer: DvRadioButtonCellRenderer,
      headerClass: 'text-center',
    },
    [ColumnType.Duration]: {
      cellStyle: { 'text-align': 'right' },
      headerClass: 'ag-right-aligned-header',
      valueFormatter: params => {
        if (!params.value) return '';

        const val = params.value;
        const d = Math.floor(val / (3600 * 24));
        const h = Math.floor((val % (3600 * 24)) / 3600);
        const m = Math.floor((val % 3600) / 60);
        const s = Math.floor(val % 60);

        const dDisplay = (d <= 9 ? '0' + d : d) + (d === 1 ? 'd ' : 'd ');
        const hDisplay = (h <= 9 ? '0' + h : h) + (h === 1 ? 'h ' : 'h ');
        const mDisplay = (m <= 9 ? '0' + m : m) + (m === 1 ? 'min ' : 'min ');
        const sDisplay = (s <= 9 ? '0' + s : s) + (s === 1 ? 's' : 's');
        return dDisplay + hDisplay + mDisplay + sDisplay;
      },
    },
    [ColumnType.TimeText]: {
      cellStyle: { 'text-align': 'right' },
      headerClass: 'ag-right-aligned-header',
      filter: 'agTextColumnFilter',
    },
    [ColumnType.TripType]: {
      headerName: 'General.TripType',
      type: [ColumnType.Text],
      filterParams: {
        maxNumConditions: 5,
        values: [...Object.keys(TripTypeIds)],
        valueFormatter: (params: ValueFormatterParams) => {
          return translateService.instant(
            `TripType.${TripTypeIds[params.value as keyof typeof TripTypeIds]}`
          );
        },
      },
      filter: 'agSetColumnFilter',
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return translateService.instant(
          `TripType.${TripTypeIds[params.value as keyof typeof TripTypeIds]}`
        );
      },
    },
    [ColumnType.UserStatus]: {
      cellRenderer: DvIconCellRenderer,
      cellRendererParams: (params: ICellRendererParams<UserDto>) => {
        const userStatusKey = params.value as
          | UserStatusTranslationKey
          | undefined;
        const isStatusSet =
          userStatusKey && userStatusKey !== UserStatusTranslationKey.NoStatus;
        return {
          icon: isStatusSet ? UserStatusIcons[userStatusKey].icon : '',
          color: isStatusSet ? UserStatusIcons[userStatusKey].color : '',
          text: isStatusSet ? translateService.instant(userStatusKey) : '',
          value: isStatusSet,
        };
      },
      valueGetter: (params: ValueGetterParams) =>
        params.data && params.colDef.field
          ? (params.data[params.colDef.field] as string)
          : '',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [...Object.keys(UserStatusIcons)],
        valueFormatter: (params: ValueFormatterParams | undefined) => {
          return params?.value ? translateService.instant(params.value) : '';
        },
      },
    },
  };
};

const caseInsensitiveTextComparator = (
  valueA: string | number | null | undefined,
  valueB: string | number | null | undefined
): number => {
  return String(valueA || '')
    .toLowerCase()
    .localeCompare(String(valueB || '').toLowerCase());
};

export const alphaNumericSortNumbersFirst = (
  valueA: string | undefined,
  valueB: string | undefined
): number => {
  if (!valueA && !valueB) return 0;
  if (!valueA) return 1;
  if (!valueB) return -1;

  const isValueANumeric = !isNaN(Number(valueA));
  const isValueBNumeric = !isNaN(Number(valueB));

  if (isValueANumeric && isValueBNumeric) {
    return Number(valueA) > Number(valueB) ? -1 : 1;
  }

  if (isValueANumeric) {
    return -1;
  }
  if (isValueBNumeric) {
    return 1;
  }

  return valueA.localeCompare(valueB);
};
